import { z } from 'zod';
export const channelKindSchema = z.enum(['public', 'private', 'dm', 'group_dm']);
export const postPolicySchema = z.enum(['all', 'admins']);
/** 'inherit' = a felhasznalo globalis ertesitesi beallitasa dont. */
/**
 * Csatornankenti ertesitesi szint.
 *
 *  - `inherit`: a felhasznalo altalanos beallitasa dont (alapertelmezes),
 *  - `always`: minden uzenet szol, munkaidon kivul is,
 *  - `all` / `mentions`: munkaidoben ez dont; munkaidon kivul a
 *    munkaidon-kivuli szint sem lehet tobb ennel,
 *  - `none`: semmi, meg az emlites sem.
 */
export const notifyLevelSchema = z.enum(['all', 'mentions', 'none', 'inherit', 'always']);
/** Csatornanev-szabaly: kisbetu, szam, kotojel, alahuzas es ekezetes betuk. */
export const CHANNEL_NAME_RE = /^[a-z0-9_-áéíóöőúüű]{1,80}$/;
export const normalizeChannelName = (raw) => raw
    .trim()
    .toLowerCase()
    .replace(/^#/, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9_\-áéíóöőúüű]/g, '');
/**
 * Egy csapat (ceg).
 *
 * A felulet ebbol epiti a cegvaltot. A lista nem tarolt adat: a sajat csapat,
 * plusz azok, ahol legalabb egy csatornanak tagja vagyok - vagyis a
 * tagsagokbol kovetkezik, es nem tud elavulni.
 */
export const teamSchema = z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
});
export const channelSchema = z.object({
    id: z.string(),
    kind: channelKindSchema,
    name: z.string().nullable(),
    topic: z.string().nullable(),
    purpose: z.string().nullable(),
    isArchived: z.boolean(),
    isGeneral: z.boolean(),
    postPolicy: postPolicySchema,
    memberCount: z.number(),
    lastSeq: z.number(),
    lastMessageAt: z.string().nullable(),
    createdAt: z.string(),
    /**
     * A letrehozo azonositoja.
     *
     * A kliensnek azert kell, hogy tudja, kinek mutassa a tagsag-kezelest: a
     * szabaly ugyanaz, mint a szerveren (letrehozo vagy admin). A felulet ezzel
     * csak ELREJTI, amit a szerver amugy is elutasitana.
     */
    createdBy: z.string().nullable(),
    /** DM-eknel a masik fel (vagy csoportos DM-nel a tobbiek) - a nev megjelenitesehez. */
    memberIds: z.array(z.string()).nullable(),
    /**
     * A DM azonosito-kulcsa: a resztvevok azonositoi rendezve.
     *
     * A kliensnek egyetlen dologra kell: felismerni a SAJAT JEGYZETEK
     * beszelgeteset, ahol a kulcs maga a felhasznalo azonositoja. Ez pontosabb,
     * mint a tagok szamabol kovetkeztetni: egy olyan DM is egytagunak LATSZIK,
     * amibol a masik fel fiokja utolag eltunt - az viszont beszelgetes volt,
     * nem jegyzet.
     *
     * Nem titok: a tagok azonositoit a kliens amugy is megkapja (`memberIds`).
     */
    dmKey: z.string().nullable(),
    /**
     * Melyik csapate a csatorna.
     *
     * A DM-eknel `null`: azok ket ember kozott allnak, nem egy cegen belul - a
     * felulet abbol vezeti le, melyik csapat nezeteben mutassa oket.
     */
    teamId: z.string().nullable(),
    /**
     * DM-eknel: MELYIK ceg nezeteben jelenjen meg.
     *
     * A DM-nek nincs sajat csapata, a feluleten viszont valahol meg kell
     * jelennie. A szabaly: ott, ahol a masik fel lathato - a sajat csapatomban,
     * ha oda tartozik, es azokban a cegekben, ahol kozos csatornank van. A
     * szerver szamolja, mert a klienshez nem jut el a tobbi csatorna tagsaga.
     *
     * Nem DM-nel `null`: ott a `teamId` mondja meg.
     */
    dmTeamIds: z.array(z.string()).nullable(),
});
/** A bejelentkezett felhasznalo sajat viszonya a csatornahoz. */
export const membershipSchema = z.object({
    isMember: z.boolean(),
    isStarred: z.boolean(),
    isMuted: z.boolean(),
    /**
     * Elrejtve az oldalsavbol. Csak a szemelyes emlites ertesit belole, es az
     * olvasatlan-jelvenybe sem szamit (lasd a `channel_members.is_hidden`-t).
     */
    isHidden: z.boolean(),
    notifyLevel: notifyLevelSchema,
    lastReadSeq: z.number(),
    /**
     * A felhasznalo sajat sorrendje az oldalsavban.
     *
     * 0 = meg nem rendezte at; ilyenkor a szokasos "legutobbi uzenet" sorrend
     * dont. Az ertek a tagsaghoz tartozik, tehat gepet valtva is megmarad.
     */
    sortOrder: z.number(),
});
export const channelWithMembershipSchema = channelSchema.extend({
    membership: membershipSchema,
    unreadCount: z.number(),
    /** Olvasatlan EMLITESEK szama - ettol lesz piros a jelzes. */
    unreadMentions: z.number(),
    /**
     * Van-e NEM FOGADOTT HIVAS, amit meg nem lattunk.
     *
     * Kulon jelzes az olvasatlan-szamtol: "kerestek" es "irtak" ket kulonbozo
     * dolog, es a hivas surgosebb. Enelkul a nem fogadott hivas egy szam lenne a
     * tobbi kozott, es pontosan ugy tunne el a szem elol, ahogy eddig.
     *
     * Nincs kulon "nyugtazas": amint elolvasod a beszelgetest, eltunik. A jelzes
     * arrol szol, hogy MEG NEM TUDSZ rola - ha mar lattad, nincs mit jelezni.
     */
    hasMissedCall: z.boolean(),
});
// ─── Keresek ──────────────────────────────────────────────────────────────────
export const createChannelInput = z.object({
    name: z.string().min(1).max(80),
    kind: z.enum(['public', 'private']),
    topic: z.string().max(250).optional(),
    purpose: z.string().max(1000).optional(),
    memberIds: z.array(z.string()).max(200).optional(),
});
export const updateChannelInput = z.object({
    name: z.string().min(1).max(80).optional(),
    /**
     * A LATHATOSAG utolag is valtoztathato - de csak a ket iranyban, ahol van
     * ertelme. DM-et es csoportos DM-et nem lehet csatornava alakitani: azoknak
     * a tagsaga maga a beszelgetes.
     *
     * A valtas KOVETKEZMENYES: nyilvanossa teve a teljes elozmeny lathatova
     * valik mindenkinek. Ezert kerul rola rendszeruzenet a csatornaba - aki
     * eddig ott beszelgetett, tudjon rola.
     */
    kind: z.enum(['public', 'private']).optional(),
    topic: z.string().max(250).nullable().optional(),
    purpose: z.string().max(1000).nullable().optional(),
    isArchived: z.boolean().optional(),
    postPolicy: postPolicySchema.optional(),
});
export const updateMembershipInput = z.object({
    isStarred: z.boolean().optional(),
    isMuted: z.boolean().optional(),
    isHidden: z.boolean().optional(),
    notifyLevel: notifyLevelSchema.optional(),
});
/**
 * Az oldalsav SAJAT sorrendje.
 *
 * A teljes sorrendet kuldjuk, nem egyetlen elem uj helyet: egy huzas utan
 * ugyis minden erintett elem szama valtozik, es egy reszleges frissites
 * konnyen hagyna lyukakat vagy utkozeseket.
 */
export const reorderChannelsInput = z.object({
    channelIds: z.array(z.string()).max(500),
});
export const openDmInput = z.object({
    userIds: z.array(z.string()).min(1).max(8),
});
/** DM-ek stabil kulcsa: a rendezett tagazonositok - ettol nyilik ugyanaz a DM ujra. */
export const buildDmKey = (userIds) => [...new Set(userIds)].sort().join(':');
/**
 * A SAJAT JEGYZETEK beszelgetese.
 *
 * ─── Miert egytagu DM, es nem uj csatorna-fajta? ────────────────────────────
 *
 * Ami kell: egy hely, ami mindenben ugy viselkedik, mint egy csatorna
 * (uzenetek, fajlok, canvas, kereses, kituzes), csak epp senki mas nem latja.
 * Egy egytagu DM PONTOSAN ez - es rogton zart is: DM-hez nem lehet tagot adni,
 * es csatlakozni sem lehet hozza (csak publikus csatornahoz lehet).
 *
 * Egy uj `notes` csatorna-fajta ugyanezt adna, cserebe minden `kind`-ra
 * illeszkedo agat at kellene irni - a jogosultsagtol a keresesig -, es minden
 * ilyen ag egy hely, ahol elfelejtodhet. Egy privat csatorna pedig azert nem
 * jo, mert a csatornanevek egyediek: "Saját jegyzetek" nem lehet mindenkie.
 *
 * A kulcs a `dmKey`, ami itt maga a felhasznalo azonositoja - ezert egy
 * embernek pontosan EGY jegyzet-beszelgetese lehet, es ezt az adatbazis
 * egyedi indexe garantalja, nem az idozites.
 */
export function isNotesChannel(channel, meId) {
    return channel.kind === 'dm' && channel.dmKey === buildDmKey([meId]);
}
//# sourceMappingURL=channel.js.map